import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import type { ButtonProps } from '../../components/Button';
import Button from '../../components/Button';
import { Cards } from '../../components/Card';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import { BaseModalContainer } from '../../components/ModalContainer';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import attachmentIcon from '../../icons/non-prefilled/16x16/attachment.svg';
import SettingCard from '../../settings/SettingCard';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSection from '../../settings/SettingsSection';
import { UIActions } from '../../ui/uiSlice';
import useNavigate from '../../utils/router/useNavigate';
import DropLine from '../DropLine';
import ConfluenceImportModal from './ConfluenceImportModal';
import DeleteTeam from './DeleteTeam';
import ImportFileModal from './ImportFileModal';
import MassExportModal from './MassExportModal';
import NotionImportModal from './NotionImportModal';

const PLATFORM_MODALS = {
  confluence: ConfluenceImportModal,
  markdown: ImportFileModal,
  notion: NotionImportModal,
} as const;

type Platform = keyof typeof PLATFORM_MODALS;

const ImportButton = (props: ButtonProps) => (
  <Button variant="secondary" {...props}>
    Import
  </Button>
);

const AdminImportExportView = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const isVerified = useAppSelector(
    (state) => state.session.organization.isVerified,
  );

  useEffect(() => {
    if (params.status === 'success') {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      navigate(`/admin/import/${params.platform}`, { replace: true });
    } else if (params.status === 'failed') {
      navigate('/admin/import', { replace: true });
    }
  }, [params.platform, params.status, navigate]);

  const showModal = (platform: Platform) =>
    navigate(`/admin/import/${platform}`);

  const exportButton = (
    <Button
      onClick={() => {
        dispatch(UIActions.openModal(MassExportModal));
      }}
      variant="secondary"
      disabled={!isVerified}
    >
      Download
    </Button>
  );

  const PlatformModal = params.platform
    ? PLATFORM_MODALS[params.platform as Platform]
    : null;

  return (
    <>
      <BaseModalContainer open={PlatformModal != null}>
        {PlatformModal && (
          <PlatformModal close={() => navigate('/admin/import')} />
        )}
      </BaseModalContainer>
      <SettingsContainerView
        title="Import and Export"
        subtitle="Transfer knowledge to Slab from other tools you use."
      >
        <SettingsSection header="Import">
          <Cards>
            <SettingCard
              description="Notion"
              icon={<ExternalBrandLogo brand="notion" height={24} />}
              actions={<ImportButton onClick={() => showModal('notion')} />}
              details="Import exported Notion content."
            />
            <SettingCard
              description="Files"
              icon={<SVGIcon size={24}>{attachmentIcon}</SVGIcon>}
              actions={<ImportButton onClick={() => showModal('markdown')} />}
              details="Upload and import HTML and Markdown files."
            />
          </Cards>
        </SettingsSection>
        <DropLine message="Want to import content from another source?" />
        <SettingsSection header="Export">
          <Cards>
            <SettingCard
              actions={
                isVerified ? (
                  exportButton
                ) : (
                  <Tooltip
                    content={
                      'Exporting is disabled\nuntil your email\nhas been verified'
                    }
                  >
                    {exportButton}
                  </Tooltip>
                )
              }
              title="Published posts"
              details="Export published posts as Markdown or Word files."
            />
          </Cards>
        </SettingsSection>
        <SettingsSection header="Danger zone">
          <Cards>
            <DeleteTeam />
          </Cards>
        </SettingsSection>
      </SettingsContainerView>
    </>
  );
};

export default AdminImportExportView;
